<template>
    <div class="baloise-insurance-accordion">
        <div class="vehicle-insurance__insurance-by">
            <span>{{ $t('pages.vehicle.insurances_by') }}</span>
            <InsuranceLogo :vendor="vendor" />
        </div>
        <Accordion
            mode="card"
            class="vehicle-insurance-accordion"
            :items="items"
            :allow-multiple-open="false"
            default-arrow-direction="right"
            slot-key="key"
        >
            <template #header-baloise_insurance="{ item }"> {{ item.priceLabel }} </template>
            <template #item-baloise_insurance>
                <p class="vehicle-insurance__list-title">
                    {{ $t('pages.booking_request.insurance_step.baloise_insurance.list1_title') }}
                </p>
                <ul>
                    <li>{{ $t('pages.booking_request.insurance_step.baloise_insurance.list1_item1') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.baloise_insurance.list1_item2') }}</li>
                </ul>

                <p class="vehicle-insurance__list-title">
                    {{ $t('pages.booking_request.insurance_step.baloise_insurance.list2_title') }}
                </p>
                <ul>
                    <li>{{ $t('pages.booking_request.insurance_step.baloise_insurance.list2_item1') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.baloise_insurance.list2_item2') }}</li>
                    <li>{{ $t('pages.booking_request.insurance_step.baloise_insurance.list2_item3') }}</li>
                </ul>
            </template>

            <template #header-roadside_assistance="{ item }">
                <VehicleAddonPrice :addon="item" />
            </template>
            <template #item-roadside_assistance>
                <p>
                    {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.description') }}
                </p>

                <div>
                    {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_title') }}
                </div>
                <ul>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item1') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item2') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item3') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item4') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item5') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item6') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item7') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.roadside_assistance_optimized.list_item8') }}
                    </li>
                </ul>
            </template>

            <template #header-deductible_buydown="{ item }">
                <VehicleAddonPrice :addon="item" />
            </template>
            <template #item-deductible_buydown>
                <i18n path="pages.booking_request.insurance_step.deductible_buydown.description">
                    <template #deductibleSubtext>
                        <strong>{{
                            $t('pages.booking_request.insurance_step.deductible_buydown.description_subtext', {
                                deductibleBefore: formattedPrice(
                                    defaultMaxDeductible,
                                    vehicle.currency,
                                    ['eur', 'chf'].includes(defaultCurrency) ? 2 : 0
                                ),
                                deductibleAfter: formattedPrice(
                                    maxDeductibleAfterBuydown,
                                    vehicle.currency,
                                    ['eur', 'chf'].includes(defaultCurrency) ? 2 : 0
                                ),
                            })
                        }}</strong>
                    </template>
                </i18n>
            </template>

            <template #header-interior_insurance="{ item }">
                <VehicleAddonPrice :addon="item" />
            </template>
            <template #item-interior_insurance>
                <ul>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.interior_insurance_optimized.list_item1') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.interior_insurance_optimized.list_item2') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.interior_insurance_optimized.list_item3') }}
                    </li>
                </ul>
            </template>

            <template #header-luggage_insurance="{ item }">
                <VehicleAddonPrice :addon="item" />
            </template>
            <template #item-luggage_insurance>
                <ul>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.luggage_insurance.list_item1') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.luggage_insurance.list_item2') }}
                    </li>
                </ul>
            </template>

            <template #header-cancellation_cost_insurance="{ item }">
                <VehicleAddonPrice :addon="item" />
            </template>
            <template #item-cancellation_cost_insurance>
                <div>
                    {{ $t('pages.booking_request.insurance_step.cancellation_cost_insurance.list_title') }}
                </div>
                <ul>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.cancellation_cost_insurance.list_item1') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.cancellation_cost_insurance.list_item2') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.cancellation_cost_insurance.list_item3') }}
                    </li>
                    <li>
                        {{ $t('pages.booking_request.insurance_step.cancellation_cost_insurance.list_item4') }}
                    </li>
                </ul>
            </template>
        </Accordion>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Accordion from '~/components/Accordion';
import InsuranceLogo from '~/components/InsuranceLogo';
import { BALOISE_ID } from '~/config/insurance';
import { PRICE_TYPE_INCLUDED } from '~/config/productTypes';
import VehicleAddonPrice from '~/components/vehicle/VehicleAddonPrice';

export default {
    name: 'BaloiseInsuranceOptions',
    components: { VehicleAddonPrice, InsuranceLogo, Accordion },
    props: {
        vehicle: {
            type: Object,
            required: true,
        },
        items: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            maxDeductibleAfterBuydown: null,
            defaultMaxDeductible: null,

            BALOISE_ID,
            PRICE_TYPE_INCLUDED,
        };
    },
    computed: {
        ...mapGetters(['defaultCurrency']),
        vendor() {
            return this.vehicle.insuranceVendor || { id: BALOISE_ID, name: 'baloise' };
        },
    },
    created() {
        const insuranceVendor = this.vehicle.insuranceVendor || {};
        this.maxDeductibleAfterBuydown = insuranceVendor.maxDeductibleAfterBuydown || null;
        this.defaultMaxDeductible = insuranceVendor.defaultMaxDeductible || null;
    },
};
</script>
